import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, MessageSquare, ArrowLeft, Copy, Check, Star, Globe, ExternalLink, BookmarkPlus, Trash2, Edit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { canUserCopyPrompt } from "@/services/subscriptionService";
import { copyPromptToClipboard, CopyResult } from "@/utils/promptCopyUtils";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SavePromptButton from "@/components/prompts/SavePromptButton";
import SharePromptButton from "@/components/prompts/SharePromptButton";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Prompt {
  id: string;
  title: string;
  description: string;
  content: string;
  category: {
    id: string;
    name: string;
    slug: string;
  };
  source?: string | null;
  likes: number;
  reviews: number;
  copies: number;
  author: {
    id: string;
    name: string;
  };
  createdAt: string;
}

interface Review {
  id: string;
  content: string;
  rating: number;
  createdAt: string;
  user: {
    id: string;
    name: string;
  };
}

const PromptDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [reviewContent, setReviewContent] = useState("");
  const [rating, setRating] = useState(5);
  const [isCopied, setIsCopied] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<string | null>(null);
  const [isDeletingReview, setIsDeletingReview] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [usageInfo, setUsageInfo] = useState<{
    canCopy: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null>(null);

  // New function to handle edit navigation
  const handleEditPrompt = () => {
    navigate(`/edit-prompt/${id}`);
  };

  useEffect(() => {
    const loadPromptDetails = async () => {
      if (!id) {
        console.error("No prompt ID provided in URL params");
        setLoadError("No prompt ID provided");
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setLoadError(null);
      
      try {
        console.log(`Loading prompt details for ID: ${id}`);
        const { data, error } = await supabase
          .from('prompts')
          .select(`
            id,
            title,
            description,
            content,
            likes,
            reviews,
            copies,
            created_at,
            user_id,
            source,
            category_id,
            categories(id, name, slug)
          `)
          .eq('id', id)
          .single();
        
        if (error) {
          console.error('Error loading prompt:', error);
          setLoadError(`Failed to load prompt: ${error.message}`);
          setIsLoading(false);
          return;
        }
        
        if (!data) {
          console.error('No prompt found with ID:', id);
          setLoadError("Prompt not found");
          setIsLoading(false);
          return;
        }
        
        console.log('Loaded prompt data:', data);
        
        // Get the author information
        const { data: authorData, error: authorError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .eq('id', data.user_id)
          .single();
        
        if (authorError) {
          console.error('Error loading author data:', authorError);
        }
        
        console.log('Author data:', authorData);
        
        setPrompt({
          id: data.id,
          title: data.title,
          description: data.description,
          content: data.content,
          source: data.source,
          category: {
            id: data.categories?.id || data.category_id,
            name: data.categories?.name || 'Uncategorized',
            slug: data.categories?.slug || 'uncategorized'
          },
          likes: data.likes || 0,
          reviews: data.reviews || 0,
          copies: data.copies || 0,
          author: {
            id: data.user_id,
            name: authorData 
              ? [authorData.first_name, authorData.last_name].filter(Boolean).join(' ') 
              : 'Anonymous'
          },
          createdAt: data.created_at
        });
        
        console.log('Formatted prompt object:', prompt);
        
        // Load reviews
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select(`
            id,
            content,
            rating,
            created_at,
            user_id
          `)
          .eq('prompt_id', id)
          .order('created_at', { ascending: false });
        
        if (reviewsError) {
          console.error('Error loading reviews:', reviewsError);
          // Don't fail completely if reviews can't be loaded
        } else if (reviewsData && reviewsData.length > 0) {
          const userIds = reviewsData.map(review => review.user_id);
          
          const { data: usersData } = await supabase
            .from('profiles')
            .select('id, first_name, last_name')
            .in('id', userIds);
          
          console.log('Users data for reviews:', usersData);
          
          const userMap = new Map();
          if (usersData) {
            usersData.forEach(user => {
              userMap.set(user.id, user);
            });
          }
          
          const formattedReviews = reviewsData.map(review => {
            const userData = userMap.get(review.user_id);
            return {
              id: review.id,
              content: review.content,
              rating: review.rating,
              createdAt: review.created_at,
              user: {
                id: review.user_id,
                name: userData 
                  ? [userData.first_name, userData.last_name].filter(Boolean).join(' ') 
                  : 'Anonymous'
              }
            };
          });
          
          setReviews(formattedReviews);
          console.log('Formatted reviews:', formattedReviews);
        }
        
        // Check if current user has liked the prompt
        if (user) {
          console.log(`Checking if user ${user.id} has liked prompt ${id}`);
          const { data: likeData, error: likeError } = await supabase
            .from('likes')
            .select('id')
            .eq('user_id', user.id)
            .eq('prompt_id', id);
          
          if (!likeError && likeData && likeData.length > 0) {
            setIsLiked(true);
            console.log('User has liked this prompt');
          } else {
            console.log('User has not liked this prompt');
          }
        }
      } catch (error: any) {
        console.error('Error loading prompt details:', error);
        setLoadError(`Error loading prompt details: ${error.message || 'Unknown error'}`);
        toast.error('Failed to load prompt details');
      } finally {
        setIsLoading(false);
      }
    };
    
    const checkUsageLimits = async () => {
      if (!user) return;
      
      try {
        console.log(`Checking copy limits for user ${user.id}`);
        const usage = await canUserCopyPrompt(user.id);
        setUsageInfo(usage);
        console.log('User copy usage:', usage);
      } catch (err) {
        console.error('Error checking usage limits:', err);
      }
    };
    
    loadPromptDetails();
    checkUsageLimits();
  }, [id, navigate, user]);

  const handleLike = async () => {
    if (!user) {
      setShowSignInDialog(true);
      return;
    }
    
    try {
      if (isLiked) {
        await supabase
          .from('likes')
          .delete()
          .eq('user_id', user.id)
          .eq('prompt_id', id);
        
        setIsLiked(false);
        setPrompt(prev => prev ? { ...prev, likes: prev.likes - 1 } : null);
        toast.success('Prompt unliked');
      } else {
        await supabase
          .from('likes')
          .insert({ user_id: user.id, prompt_id: id });
        
        setIsLiked(true);
        setPrompt(prev => prev ? { ...prev, likes: prev.likes + 1 } : null);
        toast.success('Prompt liked');
      }
    } catch (error) {
      console.error('Error updating like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleCopyContent = async () => {
    if (!prompt) return;
    
    if (!user) {
      setShowSignInDialog(true);
      return;
    }
    
    try {
      const result = await copyPromptToClipboard(id || '', prompt.content, user?.id);
      
      if (result.copied) {
        setIsCopied(true);
        toast.success('Prompt copied to clipboard');
        
        // Only update the local copy count if the copy was counted in the total on the server
        if (result.success && result.countedInTotal) {
          setPrompt(prev => prev ? { ...prev, copies: prev.copies + 1 } : null);
        }
        
        setUsageInfo(result.usageInfo);
        
        if (result.showUpgradeDialog) {
          setShowUpgradeDialog(true);
        }
        
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy prompt');
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please sign in to leave a review');
      return;
    }
    
    if (!reviewContent.trim()) {
      toast.error('Please enter a review message');
      return;
    }
    
    setIsSubmittingReview(true);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .insert({
          prompt_id: id,
          user_id: user.id,
          content: reviewContent.trim(),
          rating: rating
        })
        .select('id')
        .single();
      
      if (error) throw error;
      
      const newReview: Review = {
        id: data.id,
        content: reviewContent,
        rating: rating,
        createdAt: new Date().toISOString(),
        user: {
          id: user.id,
          name: user.user_metadata?.first_name 
            ? [user.user_metadata.first_name, user.user_metadata.last_name].filter(Boolean).join(' ')
            : 'Anonymous'
        }
      };
      
      setReviews([newReview, ...reviews]);
      setPrompt(prev => prev ? { ...prev, reviews: prev.reviews + 1 } : null);
      setReviewContent("");
      toast.success('Review submitted successfully');
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit review');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId: string) => {
    setReviewToDelete(reviewId);
    setShowDeleteDialog(true);
  };

  const confirmDeleteReview = async () => {
    if (!reviewToDelete) return;
    
    setIsDeletingReview(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewToDelete);
      
      if (error) throw error;
      
      setReviews(reviews.filter(review => review.id !== reviewToDelete));
      
      if (prompt) {
        setPrompt({
          ...prompt,
          reviews: prompt.reviews - 1
        });
      }
      
      toast.success('Review deleted successfully');
      setShowDeleteDialog(false);
      setReviewToDelete(null);
    } catch (error: any) {
      console.error('Error deleting review:', error);
      toast.error('Failed to delete review: ' + error.message);
    } finally {
      setIsDeletingReview(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatSourceUrl = (url: string) => {
    if (!url) return '';
    
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (loadError || !prompt) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <div className="flex-1 pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex justify-center items-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Prompt Not Found</h2>
            <p className="text-muted-foreground mb-6">{loadError || "The prompt you're looking for doesn't exist or has been removed."}</p>
            <Button asChild>
              <Link to="/explore">Explore Prompts</Link>
            </Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-6"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-border/40 overflow-hidden animate-fade-in">
            <div className="p-6 md:p-8">
              <div className="flex justify-between items-start gap-4 mb-4">
                <Badge variant="outline" className="px-3 py-1">
                  <Link to={`/category/${prompt.category.slug}`} className="hover:text-primary">
                    {prompt.category.name}
                  </Link>
                </Badge>
                
                <div className="flex items-center gap-4">
                  <button 
                    className={`flex items-center gap-1 text-sm ${isLiked ? 'text-red-500' : 'text-muted-foreground'} hover:text-primary transition-colors`}
                    onClick={handleLike}
                  >
                    <Heart className={`h-4 w-4 ${isLiked ? 'fill-red-500' : ''}`} />
                    <span>{prompt.likes}</span>
                  </button>
                  
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MessageSquare className="h-4 w-4" />
                    <span>{prompt.reviews}</span>
                  </div>
                  
                  <SavePromptButton promptId={prompt.id} promptTitle={prompt.title} />
                  
                  <SharePromptButton
                    promptId={prompt.id}
                    title={prompt.title}
                    description={prompt.description}
                    variant="outline"
                    size="sm"
                  />
                  
                  {/* Add Edit Button for admins */}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1"
                      onClick={handleEditPrompt}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
              
              <h1 className="text-2xl md:text-3xl font-bold mb-2">{prompt.title}</h1>
              <p className="text-muted-foreground mb-6">{prompt.description}</p>
              
              <div className="bg-secondary/50 dark:bg-secondary/30 rounded-lg p-4 md:p-6 mb-6 relative">
                <pre className="text-sm whitespace-pre-wrap font-mono">{prompt.content}</pre>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="absolute top-4 right-4 flex items-center"
                  onClick={handleCopyContent}
                >
                  {isCopied ? (
                    <Check className="h-4 w-4 mr-2" />
                  ) : (
                    <Copy className="h-4 w-4 mr-2" />
                  )}
                  {isCopied ? 'Copied!' : `Copy (${prompt.copies})`}
                </Button>
              </div>
              
              {prompt.source && (
                <div className="mb-6 flex items-center gap-2 text-sm text-muted-foreground border-l-2 border-primary/30 pl-3 py-1">
                  <Globe className="h-4 w-4" />
                  <span>Source:</span>
                  <a 
                    href={!/^https?:\/\//i.test(prompt.source) ? `https://${prompt.source}` : prompt.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    {formatSourceUrl(prompt.source)}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              
              {usageInfo && (
                <div className="flex justify-between items-center text-xs text-muted-foreground mb-4">
                  <span>
                    Prompt uses: {usageInfo.usedCount}/{usageInfo.limit}
                  </span>
                  <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${usageInfo.percentUsed > 80 ? 'bg-red-500' : 'bg-primary'}`}
                      style={{width: `${usageInfo.percentUsed}%`}}
                    />
                  </div>
                </div>
              )}
              
              <div className="flex justify-between items-center mt-4 text-sm text-muted-foreground">
                <div>
                  By <Link to={`/user/${prompt.author.id}`} className="hover:text-primary font-medium">
                    {prompt.author.name}
                  </Link>
                </div>
                <div>{formatDate(prompt.createdAt)}</div>
              </div>
            </div>
          </div>
          
          <div className="mt-12" id="reviews">
            <h2 className="text-2xl font-bold mb-6">Reviews</h2>
            
            {user ? (
              <form onSubmit={handleSubmitReview} className="mb-10">
                <div className="mb-4">
                  <div className="flex items-center gap-1 mb-2">
                    <span className="text-sm text-muted-foreground mr-2">Rating:</span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(star)}
                        className="focus:outline-none"
                      >
                        <Star 
                          className={`h-5 w-5 ${star <= rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} 
                        />
                      </button>
                    ))}
                  </div>
                  <Textarea
                    value={reviewContent}
                    onChange={(e) => setReviewContent(e.target.value)}
                    placeholder="Share your thoughts about this prompt..."
                    className="w-full"
                    rows={4}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmittingReview || !reviewContent.trim()}
                  className="ml-auto"
                >
                  {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
                </Button>
              </form>
            ) : (
              <div className="bg-secondary/30 rounded-lg p-6 mb-10 text-center">
                <p className="text-muted-foreground mb-4">Please sign in to leave a review</p>
                <Button asChild>
                  <Link to="/sign-in">Sign In</Link>
                </Button>
              </div>
            )}
            
            {reviews.length > 0 ? (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-border/40 pb-6 last:border-0">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{review.user.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {formatDate(review.createdAt)}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star}
                              className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} 
                            />
                          ))}
                        </div>
                        
                        {isAdmin && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteReview(review.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="mt-3 whitespace-pre-wrap break-words">{review.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border border-dashed border-border/40 rounded-lg">
                <p className="text-muted-foreground">
                  No reviews yet. Be the first to review this prompt!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
      
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upgrade Your Account</DialogTitle>
            <DialogDescription>
              You've reached your monthly limit of {usageInfo?.limit} prompt uses.
              Upgrade to a premium plan to get more prompts and additional features.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col space-y-4 py-4">
            <div className="flex items-center gap-4 p-4 border rounded-lg">
              <div className="bg-primary/10 p-3 rounded-full">
                <Copy className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium">Pro Plan</h4>
                <p className="text-sm text-muted-foreground">Up to 100 prompt uses per month</p>
              </div>
              <div className="font-semibold">$9.99</div>
            </div>
            
            <div className="flex items-center gap-4 p-4 border rounded-lg">
              <div className="bg-primary/10 p-3 rounded-full">
                <Copy className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium">Enterprise Plan</h4>
                <p className="text-sm text-muted-foreground">Unlimited prompt uses</p>
              </div>
              <div className="font-semibold">$29.99</div>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowUpgradeDialog(false)}
            >
              Maybe Later
            </Button>
            <Button 
              variant="default"
              onClick={() => {
                setShowUpgradeDialog(false);
                navigate('/pricing');
              }}
            >
              View Plans
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showSignInDialog} onOpenChange={setShowSignInDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sign in required</DialogTitle>
            <DialogDescription>
              You need to sign in to use this feature. Create an account to access all prompts and save your favorites.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Sign in to access all features including:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground list-disc list-inside">
              <li>Copy and use AI prompts</li>
              <li>Like and save your favorite prompts</li>
              <li>Submit your own prompts</li>
              <li>Track your prompt usage</li>
            </ul>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowSignInDialog(false)}
            >
              Maybe Later
            </Button>
            <Button 
              variant="default"
              onClick={() => {
                setShowSignInDialog(false);
                navigate('/sign-in');
              }}
            >
              Sign In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Review</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this review? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingReview}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteReview} 
              disabled={isDeletingReview} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingReview ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PromptDetailsPage;
